import * as React from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import moment from 'moment-timezone';
import { Table, Input, Button, Dropdown, Space, Modal, message } from 'antd';
import { MoreOutlined, PlusOutlined } from '@ant-design/icons';
import { useCriticalEvents } from '@src/services/queries';
import { useUpdateCriticalEvent, useDeleteCriticalEvent } from '@src/services/mutations';
import { useManagementContext } from './context';
import AssociatedFunnelAnalyses from './AssociatedFunnelAnalyses';
import * as Styles from './styled';

const Events = () => {
  const navigate = useNavigate();
  const { search: querySearch } = useLocation();
  const { setEditingPanelOpen } = useManagementContext();
  const { data, isFetching, refetch } = useCriticalEvents({
    staleTime: 0,
  });
  const { mutateAsync: updateCriticalEvent } = useUpdateCriticalEvent();
  const { mutateAsync: deleteCriticalEvent } = useDeleteCriticalEvent();
  const [search, setSearch] = React.useState<string>();
  const [loadingIds, setLoadingIds] = React.useState<string[]>([]);
  const loadingIdSet = React.useMemo(() => new Set(loadingIds), [loadingIds]);
  const [modal, contextHolder] = Modal.useModal();

  type DataItem = NonNullable<typeof data>['criticalEvents'][0];

  const columns = React.useMemo<GetProp<typeof Table<DataItem>, 'columns'>>(
    () => [
      {
        title: 'Event Name',
        dataIndex: ['name'],
        render: (value: DataItem['name'], record) => (
          <NavLink
            to={`events/${record.id}${querySearch}`}
            onClick={() => setEditingPanelOpen(true)}
          >
            {value}
          </NavLink>
        ),
        sorter: (a, b) => {
          return (a.name ?? '').localeCompare(b.name ?? '');
        },
      },
      {
        title: 'Milestone',
        dataIndex: ['is_milestone'],
        render: (value: DataItem['is_milestone']) => (value ? 'Y' : 'N'),
      },
      {
        title: 'Updated By',
        dataIndex: ['updated_by', 'email'],
        sorter: (a, b) => {
          return (a.updated_by.email ?? '').localeCompare(b.updated_by.email ?? '');
        },
      },
      {
        title: 'Last Modified',
        dataIndex: 'updated_at',
        render: (value: DataItem['updated_at']) => moment(value).format('YYYY-MM-DD HH:mm:ss'),
        sorter: (a, b) => {
          return moment(a.updated_at).unix() - moment(b.updated_at).unix();
        },
        defaultSortOrder: 'descend',
      },
      {
        title: 'Actions',
        dataIndex: 'id',
        render: (value: DataItem['id'], record) => {
          return (
            <Dropdown
              disabled={loadingIdSet.has(value)}
              menu={{
                items: [
                  { key: 'edit', label: 'Edit' },
                  {
                    key: 'explore',
                    label: 'Explore in Path View',
                  },
                  {
                    key: 'milestone-switch',
                    label: record.is_milestone ? 'Revoke Milestone' : 'Make as Milestone',
                  },
                  { key: 'delete', label: 'Delete' },
                ],
                onClick: ({ key }) => {
                  if (key === 'edit') {
                    void navigate({
                      pathname: `events/${value}`,
                      search: querySearch,
                    });
                    setEditingPanelOpen(true);
                  }

                  if (key === 'explore') {
                    void navigate(`/paths`, {
                      state: {
                        milestone: record.id,
                      },
                    });
                  }

                  if (key === 'milestone-switch') {
                    setLoadingIds((prev) => [...prev, value]);
                    void updateCriticalEvent({
                      id: value,
                      data: {
                        name: record.name,
                        is_milestone: !record.is_milestone,
                      },
                    })
                      .then(() => {
                        message.success('Critical Event updated successfully');
                      })
                      .catch(() => {
                        message.error('Failed to update Critical Event');
                      })
                      .finally(() => {
                        setLoadingIds((prev) => prev.filter((id) => id !== value));
                      })
                      .then(() => refetch());
                  }

                  if (key === 'delete') {
                    const confirmInstance = modal.confirm({
                      title: 'Are you sure you want to delete this Critical Event?',
                      content: <AssociatedFunnelAnalyses type="critical_events" id={value} />,
                      okButtonProps: { danger: true, type: 'primary' },
                      okText: 'Delete',
                      width: 560,
                      onOk: async () => {
                        setLoadingIds((prev) => [...prev, value]);
                        confirmInstance.update({ cancelButtonProps: { disabled: true } });

                        await deleteCriticalEvent(value)
                          .then(() => {
                            message.success('Critical Event deleted successfully');
                          })
                          .catch((error) => {
                            message.error(
                              (error as Error).message ?? 'Failed to delete Critical Event',
                            );
                          })
                          .finally(() => {
                            setLoadingIds((prev) => prev.filter((id) => id !== value));
                          })
                          .then(() => refetch());
                      },
                    });
                  }
                },
              }}
            >
              <Button loading={loadingIdSet.has(value)} icon={<MoreOutlined />} type="text" />
            </Dropdown>
          );
        },
      },
    ],
    [
      querySearch,
      setEditingPanelOpen,
      loadingIdSet,
      navigate,
      updateCriticalEvent,
      refetch,
      deleteCriticalEvent,
      modal,
    ],
  );

  const tableData = React.useMemo(() => {
    if (!data?.criticalEvents || !search) {
      return data?.criticalEvents;
    }

    return data.criticalEvents.filter(
      (item) =>
        item.name?.toLowerCase().includes(search.toLowerCase()) ||
        item.updated_by?.email?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [data, search]);

  return (
    <>
      {contextHolder}

      <Space direction="vertical" size={16} style={{ display: 'flex' }}>
        <Space size={16} align="center" style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Styles.TableCount>
            Total {data?.criticalEvents.length ?? 0} Critical Events
          </Styles.TableCount>
          <Button
            variant="outlined"
            color="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              void navigate({
                pathname: `events`,
                search: querySearch,
              });
              setEditingPanelOpen(true);
            }}
          >
            Critical Event
          </Button>
          <Input.Search allowClear placeholder="Search..." onSearch={setSearch} />
        </Space>
        <Table
          rowKey="id"
          dataSource={tableData}
          columns={columns}
          loading={isFetching}
          scroll={{ x: true }}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          sortDirections={['ascend', 'descend', 'ascend']}
        />
      </Space>
    </>
  );
};

export default React.memo(Events);
